import template from './bProcessGroupChart.html';
import moment from 'moment';
import d3 from 'd3';

const COLOR_NVA = '#FF644B';
const COLOR_VA = '#00A990';
const COLOR_SELECT = '#5479BD';

export function bProcessGroupChartDirective() {
  'ngInject';
  return {
    restrict: 'E',
    template: template,
    controller: bProcessGroupChartController,
    controllerAs: 'bProcessGroupChart',
    bindToController: true,
    scope: {
      tooltipId: "@",
      data: "=",
      canShare: "=",
      onShareChart: "&",
      onExportChart: "&",
      onProcessGroup: "&"
    }
  };
}

class bProcessGroupChartController {
  constructor($filter, $scope, $translate, $routeParams, $rootScope) {
    'ngInject';
    this.$filter = $filter;
    this.$scope = $scope;
    this.$rootScope = $rootScope;
    this.$translate = $translate;
    this.$routeParams = $routeParams;
    this.seriesVisibility = { series0: true, series1: true };
    this.labelData = [];
    this.chartData = [];
    this.maxYValue = 0;
    this.singleValue = false;
    this.selectedGroup = '';
    this.updateChartOptions();
    this.$scope.$watch(() => this.data, newValue => this.updateChart(newValue));
    this.$rootScope.toggleValueProcessGroupChart = 'All';
  }

  exportChart() {
    this.onExportChart({ exportData: { type: 'ProcessGroup' } });
  }

  getCharIdAt(i) {
    return (i >= 26 ? this.getCharIdAt((i / 26 >> 0) - 1) : '') + 'abcdefghijklmnopqrstuvwxyz'[i % 26 >> 0];
  }

  getMaxValue(data) {
    if (!data || !data.length) {
      return 0;
    }

    const hoursCeil = Math.ceil(moment.duration(data[0].sum).asHours());
    return hoursCeil * 1000 * 3600;
  }

  getRectTooltipPosition(node) {
    const bcr = node.getBoundingClientRect();
    return { top: bcr.top - 38, left: bcr.left - 30 };
  }

  shareChart() {
    const shareData = { type: 'process_group' };
    this.onShareChart({ shareData });
  }

  updateChartOptions() {
    var self = this;
    this.$rootScope.exportProcessGroupChart = this.data;
    const tooltipId = this.tooltipId;
    const filter = this.$filter;
    const chartSizeConst = {
      xTickPadding: 28,
      yTickPadding: 15,
      xLabelHeight: 20,
      chartHeight: 280
    };
    this.chartOptions = {
      chart: {
        type: 'multiBarChart',
        noData: this.$translate.instant('NOTHING_TO_SHOW'),
        height: chartSizeConst.chartHeight,
        stacked: true,
        showControls: false,
        reduceXTicks: false,
        yDomain: [0, this.maxYValue ? this.maxYValue : 1],
        margin: {
          left: 70,
          right: 60,
          top: 0,
          bottom: chartSizeConst.xLabelHeight + chartSizeConst.xTickPadding
        },
        multibar: {
          dispatch: {
            elementClick: (e) => {
              const bars = d3.selectAll('.bProcessGroupChart__content rect.nv-bar');
              if (bars[0]) {
                const currLen = self.labelData.length;
                const currentSelected = self.labelData[e.index] === self.selectedGroup;
                bars[0].forEach((bar, idx) => {
                  if (idx === e.index || idx === currLen + e.index) {
                    bar.style.fillOpacity = currentSelected ? '' : '1';
                    bar.style.fill = currentSelected ? '' : COLOR_SELECT;
                  } else {
                    bar.style.fillOpacity = '';
                    bar.style.fill = '';
                  }
                });
                self.selectedGroup = currentSelected ? '' : self.labelData[e.index];
                self.onProcessGroup({ processGroup: self.selectedGroup });
              }
            },
          }
        },
        tooltip: {
          position: () => {
            const tooltipEl = angular.element(document.getElementById(`view-360-tooltip-${tooltipId}`));
            const parent = tooltipEl.parent();
            if (tooltipEl && parent && !parent.hasClass('view-360-tooltip')) {
              parent.addClass('view-360-tooltip');
            }

            const rect = angular.element(document.getElementsByClassName('hover'));
            if (!rect || !rect.length) {
              return {};
            }

            return this.getRectTooltipPosition(rect[0]);
          },
          contentGenerator: function (d) {
            const label = self.labelData[d.index];
            const value = filter('hm')(moment.duration(d.series[0].value).toJSON());
            return `
                <div id="view-360-tooltip-${tooltipId}" class="view-360-tooltip-inner">
                 <div>${label}</div>
                 <div>${value}</div>
                </div>`;
          }
        },
        xAxis: {
          tickPadding: chartSizeConst.xTickPadding,
          tickValues: d => d[0].values.map(ym => ym.x),
          tickFormat: (d, i) => {
            return self.singleValue ?
              self.labelData[i] ? 'A' : ''
              : this.getCharIdAt(i).toUpperCase();
          }
        },
        yAxis: {
          showMaxMin: true,
          tickPadding: chartSizeConst.yTickPadding,
          tickValues: d => {
            const max = moment.duration(this.maxYValue).asHours();
            const ticksCount = Math.min(d[0].values.length, 6);
            let tickVal = parseInt(max / ticksCount);
            tickVal = tickVal ? tickVal : 1;
            const tickVals = [0];
            if (max === 0 || tickVal === 0) {
              return [0];
            }
            for (let t = tickVal; t <= max; t += tickVal) {
              tickVals.push(t);
            }
            return tickVals.map(val => val * 1000 * 3600);
          },
          tickFormat: d => {
            if (d < 0 || (d > 0 && d < 1000)) {
              return '';
            }
            return `${moment.duration(d).asHours()} h`;
          }
        }
      }
    };
  }

  updateChart(newValue) {
    if (!newValue) {
      return;
    }

    const processGroupMap = {};
    const data = [
      {
        key: 'VA',
        color: COLOR_VA,
        values: []
      },
      {
        key: 'NVA',
        color: COLOR_NVA,
        values: [],
      }
    ];

    newValue.forEach(pg => {
      const groupName = pg.processGroupName.replace(/\s/g, '').toLowerCase();
      if (!processGroupMap[groupName]) {
        processGroupMap[groupName] = {
          vaSum: 0,
          nvaSum: 0,
          sum: 0,
          name: pg.processGroupName
        };
      }

      processGroupMap[groupName].vaSum += moment.duration(pg.vaDuration).asMilliseconds();
      processGroupMap[groupName].nvaSum += moment.duration(pg.nvaDuration).asMilliseconds();
      processGroupMap[groupName].sum = processGroupMap[groupName].nvaSum + processGroupMap[groupName].vaSum;
    });

    this.labelData = [];
    const sorted = Object.values(processGroupMap).sort((a, b) => b.sum - a.sum);
    sorted.forEach((obj, idx) => {
      data[0].values.push({ x: idx, y: obj.vaSum });
      data[1].values.push({ x: idx, y: obj.nvaSum });
      this.labelData.push(obj.name);
    });

    this.maxYValue = this.getMaxValue(sorted);
    this.singleValue = data[0].values.length === 1;
    if (this.singleValue) {
      data[0].values.unshift({ x: -1, y: null });
      data[0].values.push({ x: 1, y: null });
      data[1].values.unshift({ x: -1, y: null });
      data[1].values.push({ x: 1, y: null });
      this.labelData.unshift(null);
      this.labelData.push(null);
    }

    if (!this.maxYValue) {
      this.chartData = [
        {
          key: 'VA',
          color: COLOR_VA,
          values: []
        },
        {
          key: 'NVA',
          color: COLOR_NVA,
          values: []
        }
      ];
      return;
    }
    this.chartData = data;
    this.updateChartOptions();
  }

  hasSharedView() {
    return window.location.pathname.includes('shared');
  }

  toggleLegend(type) {
    if (!this.hasSharedView()) {
      this.seriesVisibility[type] = !this.seriesVisibility[type];
      if (!this.seriesVisibility['series0'] && !this.seriesVisibility['series1']) {
        this.seriesVisibility['series0'] = true;
        this.seriesVisibility['series1'] = true;
      }
      const nthChild = type === 'series0' ? 1 : 2;

      if (this.seriesVisibility['series0'] && this.seriesVisibility['series1']) this.$scope.value = "All";
      if (this.seriesVisibility['series0'] && !this.seriesVisibility['series1']) this.$scope.value = "Va";
      if (!this.seriesVisibility['series0'] && this.seriesVisibility['series1']) this.$scope.value = "Nva";

      this.$rootScope.toggleValueProcessGroupChart = this.$scope.value;

      const legend = d3.select(`.bProcessGroupChart .nv-series:nth-child(${nthChild})`).node();
      legend.dispatchEvent(new MouseEvent('click'));
      if (this.selectedGroup) {
        this.selectedGroup = '';
        this.onProcessGroup({ processGroup: this.selectedGroup });
      }

    }
  }
}
